import {useAppDispatch, useDimensions, useDimensionsPending} from "../../../../redux/stateHooks";
import {useAppContext} from "../../../../hooks/useAppContext";
import {useMinimumQuantity} from "../../../../hooks/useMinimumQuantity";
import * as React from "react";
import {Suspense, useMemo, useState} from "react";
import {useScreenSizeDetector} from "../../../../hooks/useScreenSizeDetector";
import {CommonCardLayout} from "./commonCardLayout";
import {Button, InputGroup} from "react-bootstrap";
import {BsCheckLg} from "react-icons/bs";
import {artworkOffCanvasActions} from "../../../../redux/slices/artworkOffCanvas";
import {Favorites} from "../favorites";
import {QuantityInputGroup} from "./quantityInputGroup";
import {DesignServices} from "./designServices";
import {useProductDetailsQuery} from "../../../../hooks/useProductDetailsQuery";
import {useCreateItem} from "../../../../hooks/addToCart/useCreateItem";
import {MinimumQuantityNotice} from "./minimumQuantityNotice";
import {useAddToCartDisabled} from "../../../../hooks/addToCart/useAddToCartDisabled";
import {clearArtworkFile, clearArtworkNotes} from "../../../../redux/slices/addToCart";
import {useQuantity} from "../../../../hooks/useQuantity";
import {getDesignerPageUrl} from "../../../../utils/helpers";

const sendArtworkLater = "Send Artwork Later";
const uploadArtworkDesignOnline = "Upload Artwork";
const DesignOnline = "Design Online"

const designOnline = "design-online";
const sendLater = "send-later";
const uploadArtwork = "upload-artwork";

export const DesignerProduct = () => {
    const dispatch = useAppDispatch()
    const {productId, primaryId} = useAppContext();
    const [selectedOption, setSelectedOption] = useState<'design-online' | 'send-later' | 'upload-artwork'>(uploadArtwork);
    const {isSmallScreenSize} = useScreenSizeDetector();
    const {minimumSaleQuantity} = useMinimumQuantity();
    const {quantity} = useQuantity();
    const addToCartDisabled = useAddToCartDisabled()
    const {createItem} = useCreateItem()
    const {width, height} = useDimensions();
    const {model: {slug, sku, artworkGuidelineTypePath, dynamicDimensions: {isDynamic}}} = useProductDetailsQuery();

    const sendLaterIsSelected = useMemo(() => selectedOption === sendLater, [selectedOption]);
    const designerIsSelected = useMemo(() => selectedOption === designOnline, [selectedOption]);
    const uploadIsSelected = useMemo(() => selectedOption === uploadArtwork, [selectedOption]);

    const designerPageUrl = getDesignerPageUrl(primaryId, productId, slug, isDynamic, width, height);

    return (
        <CommonCardLayout isSmallScreenSize={isSmallScreenSize}>
            <div>Artwork Options:{' '}
                <span className="fw-bold">
                    {uploadIsSelected && uploadArtworkDesignOnline}
                    {designerIsSelected && DesignOnline}
                    {sendLaterIsSelected && sendArtworkLater}
                </span>
            </div>
            <div className="product-options-wrapper d-flex mt-3">
                <Button variant="outline-secondary"
                        size="sm"
                        className={`text-center rounded-9 mx-1 text-dark ${uploadIsSelected && ' active position-relative'}`}
                        onClick={() => setSelectedOption(uploadArtwork)}
                        disabled={uploadIsSelected}
                        aria-disabled={uploadIsSelected}
                >
                    {uploadArtworkDesignOnline}
                    {uploadIsSelected && <BsCheckLg className="position-absolute top-0 end-0 bg-blue" aria-hidden/>}
                </Button>
                <Button variant="outline-secondary"
                        size="sm"
                        className={`text-center rounded-9 mx-1 text-dark ${designerIsSelected && ' active position-relative'}`}
                        onClick={() => setSelectedOption(designOnline)}
                        disabled={designerIsSelected}
                        aria-disabled={designerIsSelected}>
                    {DesignOnline}
                    {designerIsSelected && <BsCheckLg className="position-absolute top-0 end-0 bg-blue" aria-hidden/>}
                </Button>
                <Button variant="outline-secondary"
                        size="sm"
                        className={`text-center rounded-9 mx-1 text-dark ${sendLaterIsSelected && 'active position-relative'}`}
                        onClick={() => {
                            dispatch(clearArtworkFile());
                            dispatch(clearArtworkNotes());
                            setSelectedOption(sendLater);
                        }}
                        disabled={sendLaterIsSelected}
                        aria-disabled={sendLaterIsSelected}>
                    {sendArtworkLater}
                    {sendLaterIsSelected && <BsCheckLg className="position-absolute top-0 end-0 bg-blue" aria-hidden/>}
                </Button>
            </div>
            <div className="my-3 ms-2 d-flex align-items-center artwork-options-descriptions">
                {sendLaterIsSelected &&
                    <div className="d-block">
                        <div className="mb-2">
                            Checkout now and we'll send you a link to upload your artwork later.
                        </div>
                        <Suspense fallback={<></>}>
                            <DesignServices location='page'/>
                        </Suspense>
                    </div>
                }
                {uploadIsSelected && (
                    <div className="d-block">
                        <div className="mb-2">
                            Upload your finished artwork and checkout.
                            {artworkGuidelineTypePath && (
                                <a href={artworkGuidelineTypePath} target="_blank" className="d-block" tabIndex={0}
                                   role="link" aria-label="View templates & guidelines">
                                    <small>
                                        View templates & guidelines
                                    </small>
                                </a>
                            )}
                        </div>
                    </div>
                )}
                {designerIsSelected &&
                    <span className="d-block">
                        Using our online design tool you can create visually stunning artwork with ease. Proof instantly and checkout.
                    </span>
                }
            </div>
            <InputGroup>
                {sendLaterIsSelected && <QuantityInputGroup/>}
                {designerIsSelected &&
                    <a href={designerPageUrl}
                       className="flex-fill add-to-cart-btn ms-1 btn btn-warning btn-lg" id="fyrefly-design-submit"
                       tabIndex={0} role="link" aria-label="Start Designing Online">
                        Start Designing Online
                    </a>
                }
                {uploadIsSelected && (
                    <Button
                        aria-label="Continue to Upload Artwork"
                        variant="warning"
                        size="lg"
                        onClick={() => dispatch(artworkOffCanvasActions.setShow(true))}
                        className="flex-fill add-to-cart-btn ms-1"
                    >
                        Continue to Upload Artwork
                    </Button>
                )}
                {sendLaterIsSelected &&
                    <Button
                        disabled={addToCartDisabled}
                        aria-label="Add to Cart"
                        variant="warning"
                        size="lg"
                        onClick={async () => await createItem()}
                        className="flex-fill add-to-cart-btn ms-1"
                        data-ee-list={`ProductNew | Details`}
                        data-react="true"
                        data-item-id={productId}
                        data-quantity={quantity}
                        data-sku={sku}
                    >
                        Add to Cart
                    </Button>
                }
                <Favorites/>
            </InputGroup>
            {(sendLaterIsSelected && minimumSaleQuantity > 1) &&
                <MinimumQuantityNotice minimumQuantity={minimumSaleQuantity}/>}
        </CommonCardLayout>
    );
};